const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  status?: number;
}

// Core API functions for follows
const apiCall = async <T>(
  token: string | null,
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    const headers: HeadersInit = {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/v2${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.error?.message ||
        errorData.message ||
        `HTTP ${response.status}: ${response.statusText}`;

      const { logWarning } = await import("@/lib/client/errors");
      logWarning("Follow API error", {
        status: response.status,
        statusText: response.statusText,
        errorData: JSON.stringify(errorData, null, 2),
        endpoint,
      });

      return {
        success: false,
        error: errorMessage,
        code: errorData.error?.code || errorData.code,
        status: response.status,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData.data || responseData,
    };
  } catch (error) {
    const { logError } = await import("@/lib/client/errors");
    logError(error as Error, { endpoint });
    return {
      success: false,
      error: (error as Error).message,
    };
  }
};

export const followsApi = {
  // Brand follows
  followBrand: async (token: string | null, brandId: number): Promise<ApiResponse<unknown>> => {
    return apiCall(token, `/user/brands/${brandId}/follow`, {
      method: "PUT",
    });
  },

  unfollowBrand: async (token: string | null, brandId: number): Promise<ApiResponse<unknown>> => {
    return apiCall(token, `/user/brands/${brandId}/follow`, {
      method: "DELETE",
    });
  },

  getFollowedBrands: async (
    token: string | null
  ): Promise<ApiResponse<{ brands: BrandFilter[] }>> => {
    return apiCall<{ brands: BrandFilter[] }>(token, "/user/brands/follows");
  },

  // Vendor follows
  followVendor: async (token: string | null, vendorId: number): Promise<ApiResponse<unknown>> => {
    return apiCall(token, `/user/vendors/${vendorId}/follow`, {
      method: "PUT",
    });
  },

  unfollowVendor: async (token: string | null, vendorId: number): Promise<ApiResponse<unknown>> => {
    return apiCall(token, `/user/vendors/${vendorId}/follow`, {
      method: "DELETE",
    });
  },

  getFollowedVendors: async (token: string | null): Promise<ApiResponse<{ vendors: Vendor[] }>> => {
    return apiCall<{ vendors: Vendor[] }>(token, "/user/vendors/follows");
  },

  checkUnsyncedFilters: async (token: string) => {
    const res = await fetch(`${API_URL}/api/v2/user/check-unsynced-filters`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return res.json();
  },

  migrateFiltersToFollows: async (
    token: string | null
  ): Promise<
    ApiResponse<{
      brandsFollowed: number;
      vendorsFollowed: number;
      message: string;
    }>
  > => {
    return apiCall(token, "/user/migrate-filters-to-follows", {
      method: "POST",
    });
  },
};
